import type { ReactNode, Ref, RefObject } from 'react';
import { useRef } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import {
  IconButton,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'design-system';
import { Plus } from 'lucide-react';

import { useOverflowObserver } from '@/shared/hooks/useOverflowObserver';
import { PageLayout } from '@/shared/ui/layout';

export type DashboardTab = {
  name: string;
  id?: string;
  disabled?: boolean;
  label?: ReactNode;
  tag?: ReactNode;
  sortableHandleRef?: RefObject<HTMLElement | null>;
};

const Title = ({ title }: { title: ReactNode }) => {
  const [titleRef, isOverflowed] = useOverflowObserver<HTMLHeadingElement>([title]);

  if (typeof title !== 'string' && typeof title !== 'number') {
    return <span>{title}</span>;
  }

  const heading = (
    <h1
      ref={titleRef}
      className='mr-4 min-w-0 shrink truncate font-heading text-2xl font-semibold'
    >
      {title}
    </h1>
  );

  if (!isOverflowed) return heading;

  return (
    <Tooltip>
      <TooltipTrigger render={heading} />
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
};

const configuredTabClassName =
  'relative flex h-12 items-center justify-center whitespace-nowrap border border-transparent text-sm font-medium text-foreground/60 transition-colors after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:bg-foreground after:opacity-0 focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 data-[active=true]:text-foreground data-[active=true]:after:opacity-100';

const TabButton = ({
  ref,
  activeTab,
  index,
  minTabWidth,
  tab,
  onTabChange
}: {
  ref?: Ref<HTMLDivElement>;
  activeTab: number;
  index: number;
  minTabWidth: number;
  tab: DashboardTab;
  onTabChange: (index: number) => void;
}) => (
  <div
    ref={ref}
    className={configuredTabClassName}
    data-active={activeTab === index}
    style={{ minWidth: `${minTabWidth}px` }}
    onMouseDownCapture={event => {
      if (!tab.disabled && activeTab !== index) {
        event.stopPropagation();
        onTabChange(index);
      }
    }}
  >
    <button
      aria-label={tab.name}
      aria-selected={activeTab === index}
      className='sr-only'
      disabled={tab.disabled}
      role='tab'
      tabIndex={activeTab === index ? 0 : -1}
      type='button'
      onClick={() => {
        onTabChange(index);
      }}
    >
      {tab.name}
    </button>
    {tab.label ?? tab.name}
    {tab.tag}
  </div>
);

const ConfiguredTab = (props: {
  activeTab: number;
  index: number;
  minTabWidth: number;
  tab: DashboardTab;
  onTabChange: (index: number) => void;
}) => <TabButton {...props} />;

const SortableConfiguredTab = ({
  activeTab,
  container,
  index,
  minTabWidth,
  tab,
  onTabChange
}: {
  activeTab: number;
  container: RefObject<HTMLDivElement | null>;
  index: number;
  minTabWidth: number;
  tab: DashboardTab & { id: string; sortableHandleRef: RefObject<HTMLElement | null> };
  onTabChange: (index: number) => void;
}) => {
  const { ref } = useSortable({
    id: tab.id,
    index,
    handle: tab.sortableHandleRef,
    type: 'dashboard-tab',
    accept: ['dashboard-tab'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  return (
    <TabButton
      ref={ref}
      activeTab={activeTab}
      index={index}
      minTabWidth={minTabWidth}
      tab={tab}
      onTabChange={onTabChange}
    />
  );
};

export const DashboardLayout = ({
  title,
  children,
  renderRight,
  stickyHeader = true,
  tabNames = [],
  activeTab = 0,
  onTabChange,
  onAddTab,
  tabsConfig,
  minTabWidth = 140
}: {
  minTabWidth?: number;
  stickyHeader?: boolean;
  title: ReactNode;
  children?: ReactNode;
  renderRight?: ReactNode;
  tabNames?: string[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  onAddTab?: () => void;
  tabsConfig?: DashboardTab[];
  disableTabRipple?: boolean;
}) => {
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsToRender: DashboardTab[] = tabsConfig ?? tabNames.map(name => ({ name }));

  return (
    <PageLayout
      actions={renderRight}
      stickyHeader={stickyHeader}
      tabs={
        <div
          ref={tabsContainerRef}
          className='min-w-0 flex-1'
        >
          {tabsToRender.length > 0 && onTabChange && (
            <div className='flex min-w-0 items-center gap-1'>
              {tabsConfig ? (
                <div
                  aria-label='Dashboard tabs'
                  className='flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                  role='tablist'
                >
                  {tabsToRender.map((tab, index) =>
                    tab.id && tab.sortableHandleRef ? (
                      <SortableConfiguredTab
                        key={tab.id}
                        activeTab={activeTab}
                        container={tabsContainerRef}
                        index={index}
                        minTabWidth={minTabWidth}
                        tab={{
                          ...tab,
                          id: tab.id,
                          sortableHandleRef: tab.sortableHandleRef
                        }}
                        onTabChange={onTabChange}
                      />
                    ) : (
                      <ConfiguredTab
                        key={tab.id ?? `${tab.name}-${index}`}
                        activeTab={activeTab}
                        index={index}
                        minTabWidth={minTabWidth}
                        tab={tab}
                        onTabChange={onTabChange}
                      />
                    )
                  )}
                </div>
              ) : (
                <Tabs
                  className='min-w-0 flex-1'
                  value={String(activeTab)}
                  onValueChange={value => {
                    onTabChange(Number(value));
                  }}
                >
                  <TabsList
                    aria-label='Dashboard tabs'
                    className='max-w-full'
                    variant='line'
                  >
                    {tabsToRender.map((tab, index) => (
                      <TabsTrigger
                        key={tab.id ?? `${tab.name}-${index}`}
                        disabled={tab.disabled}
                        style={{ minWidth: `${minTabWidth}px` }}
                        value={String(index)}
                      >
                        {tab.label ?? tab.name}
                        {tab.tag}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              )}
              {onAddTab && (
                <IconButton
                  label='Add tab'
                  size='icon-sm'
                  variant='outline'
                  onClick={onAddTab}
                >
                  <Plus />
                </IconButton>
              )}
            </div>
          )}
        </div>
      }
      title={<Title title={title} />}
    >
      {children}
    </PageLayout>
  );
};
